import { useDigitalTwin } from 'api/digitalTwin/digitalTwin.api'
import { parseTags } from 'helpers/filterComponent.helper/filterComponent.helper'
import { useSetActiveFiltersInUrl } from 'hooks/filterHooks/filterHooks'
import React, { useCallback, useMemo } from 'react'
import filterStore from 'store/filter/filter'
import { useSnapshot } from 'valtio'
import { useAuth } from '../AuthContext/AuthContext'
import FilterDropdownsGroup from './FilterDropdownsGroup'

type FilterComponentProps = {
  availableFiltersSubset: string[][] // The list of filters to show in the dropdowns. This should be a subset of 'filterStore.allTags'
  activeTags: Set<string>
  topLevelSortingOrderOverride?: string[]
}

export default function FilterComponent({
  availableFiltersSubset,
  activeTags,
  topLevelSortingOrderOverride,
}: FilterComponentProps): React.JSX.Element {
  const { allTags } = useSnapshot(filterStore)
  const { activeSystem } = useAuth()
  const primaryDigitalTwin = activeSystem?.primary_digital_twin
  const { data: digitalTwin } = useDigitalTwin(primaryDigitalTwin?.uid)
  const translations = digitalTwin?.translations

  const setActiveFiltersInUrl = useSetActiveFiltersInUrl()

  const handleOnApply = useCallback(
    (topLevelTag: string, filtersToBeApplied: Record<string, boolean>) => {
      const activeFilters = Object.keys(filtersToBeApplied).filter((key) => filtersToBeApplied[key])
      setActiveFiltersInUrl(topLevelTag, activeFilters)
    },
    [setActiveFiltersInUrl]
  )

  const options = useMemo(() => {
    const tagsFromSubset = availableFiltersSubset.flat()
    return parseTags(Array.from(new Set([...Array.from(allTags), ...tagsFromSubset])))
  }, [allTags, availableFiltersSubset])

  const values = useMemo(() => {
    return Array.from(activeTags)
  }, [activeTags])

  return (
    <FilterDropdownsGroup
      options={options}
      onSelected={handleOnApply}
      values={values}
      translations={translations}
      hiddenFilters={digitalTwin?.model?.hidden_default_tags}
      topLevelSortingOrder={topLevelSortingOrderOverride ?? digitalTwin?.model?.tag_sorting_order}
      availableFiltersSubset={availableFiltersSubset}
    />
  )
}
